'use client'

import LINKS from 'src/_helpers/links'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import Typography from 'src/components/design-system/Typography'
import { JournalArticleItem } from 'src/components/items/JournalArticleItem'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Button from 'src/components/design-system/Button'
import DownloadIcon from 'public/icons/interface/download.svg'
import FileIcon from 'public/icons/interface/file2.svg'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'

gsap.registerPlugin(useGSAP)

const ARTICLES_STEP = 8

// Rozwinięcie listy trafia do adresu, żeby dało się do niego wrócić po odświeżeniu strony.
const VISIBLE_ARTICLES_PARAM = 'artykuly'

const formatDate = (rawDate) => {
  if (!rawDate) return null

  const date = new Date(rawDate)
  if (!Number.isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  if (typeof rawDate === 'string') {
    const normalized = rawDate.replace(/\//g, '.').trim()
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(normalized)) {
      return normalized
    }
    return normalized
  }

  return null
}

export default function NumberContent({ block, locale, issue, routeContext = 'main' }) {
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const [visibleArticles, setVisibleArticles] = useState(ARTICLES_STEP)
  const tileRefs = useRef([])
  const previousVisibleArticlesRef = useRef(ARTICLES_STEP)

  const articles = Array.isArray(block?.articles) ? block.articles : []
  const articlesCount = block.articlesCount || 0
  const isDomainJournal = routeContext === 'journal-domain'

  // Liczba rozwiniętych artykułów żyje w adresie, żeby odświeżenie strony (albo powrót z artykułu)
  // nie kasowało kliknięć w „Pokaż więcej”. Czytamy `window.location` zamiast `useSearchParams`,
  // bo ten drugi wymusiłby otoczenie bloku boundary'em `Suspense`.
  const readVisibleArticlesFromUrl = () => {
    if (typeof window === 'undefined') return null

    const rawValue = Number(new URLSearchParams(window.location.search).get(VISIBLE_ARTICLES_PARAM))

    if (!Number.isFinite(rawValue) || rawValue <= ARTICLES_STEP) return null

    return Math.min(Math.ceil(rawValue / ARTICLES_STEP) * ARTICLES_STEP, Math.max(articlesCount, articles.length))
  }

  // Stan z adresu ustawiamy dopiero po zamontowaniu — inaczej pierwszy render klienta różniłby się
  // od tego z serwera i React zgłosiłby błąd hydracji.
  useEffect(() => {
    const restored = readVisibleArticlesFromUrl() ?? ARTICLES_STEP

    setVisibleArticles(restored)
    // Ustawienie referencji z góry pomija animację wejścia dla przywróconych kafelków.
    previousVisibleArticlesRef.current = restored
    tileRefs.current = []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.uuid])

  // Obsługa przycisków wstecz/dalej w przeglądarce.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const handlePopState = () => {
      const restored = readVisibleArticlesFromUrl() ?? ARTICLES_STEP

      setVisibleArticles(restored)
      previousVisibleArticlesRef.current = restored
    }

    window.addEventListener('popstate', handlePopState)

    return () => window.removeEventListener('popstate', handlePopState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles.length, articlesCount])

  const showMoreArticles = () => {
    const nextVisibleArticles = visibleArticles + ARTICLES_STEP

    setVisibleArticles(nextVisibleArticles)

    if (typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)

    params.set(VISIBLE_ARTICLES_PARAM, String(nextVisibleArticles))

    // replaceState, a nie pushState — „wstecz” ma wracać do poprzedniej strony,
    // a nie odklikiwać kolejne rozwinięcia listy.
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`)
  }

  useGSAP(() => {
    const previousVisibleArticles = previousVisibleArticlesRef.current

    if (visibleArticles <= previousVisibleArticles) {
      previousVisibleArticlesRef.current = visibleArticles
      return
    }

    const newTiles = tileRefs.current.slice(previousVisibleArticles, visibleArticles).filter(Boolean)

    if (newTiles.length > 0) {
      gsap.fromTo(
        newTiles,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: 0.45,
          stagger: 0.06,
          ease: 'power2.out',
          clearProps: 'opacity,visibility',
        },
      )
    }

    previousVisibleArticlesRef.current = visibleArticles
  }, [visibleArticles])

  const blockTranslation = getPreferredTranslation(block, locale) || {}
  const blockTitle = blockTranslation?.title?.trim() || translate('T_BLOCK_ISSUE_CONTENT_TITLE')
  return (
    <section className={`my-64 d:px-40 px-16 ${block.type.value}`} id={block.uuid}>
      <div className="pb-32 flex items-center justify-between">
        {blockTitle && (
          <Typography variant="h2-caps-bold" color="text-basic-black">
            {blockTitle}
          </Typography>
        )}
        <Link
          href={`${process.env.NEXT_PUBLIC_API_ENTRYPOINT}/open/journal_issues/${issue?.uuid}/articles-pdf`}
          className="flex items-center gap-x-14 group"
        >
          <FileIcon className={isDomainJournal ? '[&_path]:stroke-basic-black' : ''} />
          <Typography
            variant="h3-light"
            color={isDomainJournal ? 'text-basic-black' : 'text-dark-blue-dark-blue2'}
            component="span"
            className={`block w-fit h-fit relative after:content-[''] after:h-1 after:w-0 after:transition-[width] after:absolute after:bottom-0 after:left-0 group-hover:after:w-full ${
              isDomainJournal ? 'after:bg-basic-black' : 'after:bg-dark-blue-dark-blue2'
            }`}
          >
            {translate('T_DOWNLOAD_FREE_ARTICLES')}
          </Typography>
          <DownloadIcon className={isDomainJournal ? '[&_path]:stroke-basic-black' : ''} />
        </Link>
      </div>

      <ul
        className={`grid gap-x-42 gap-y-24 t:grid-cols-2 d:grid-cols-4 border-y py-16 ${
          isDomainJournal ? 'border-y-basic-gray7' : 'border-y-dark-blue-dark-blue6'
        }`}
      >
        {articles.slice(0, visibleArticles).map((item, i) => {
          const article = item

          const articleTranslation = getPreferredTranslation(article, locale) || {}
          const journalTranslation = getPreferredTranslation(article?.journal, locale) || {}
          const issueTranslation = getPreferredTranslation(article?.issue, locale) || {}
          const articleTitle = articleTranslation?.title || ''
          const date = formatDate(article?.publicationDate)
          const slug = articleTranslation?.slug
          const journalSlug = journalTranslation?.slug
          const parentSlug = issueTranslation?.slug
          const articleRoute =
            routeContext === 'journal-domain' ? journalRoutes.ISSUES.DETAIL_DETAIL : LINKS.ISSUES.DETAIL_DETAIL
          const url =
            buildLocalizedPath(articleRoute, locale, {
              slug: journalSlug,
              issue: parentSlug,
              article: slug,
            }) || '#'

          const tags = Array.isArray(articleTranslation?.tags)
            ? articleTranslation.tags.map((label) => {
                const normalized = String(label).toLowerCase()
                const type = normalized.includes('nowość') || normalized.includes('nowosc') ? 'new' : 'default'
                return { label, type }
              })
            : []

          const authors = (article.authors || []).map((a) => a?.fullName).filter(Boolean)

          return (
            <li
              key={i}
              ref={(el) => {
                tileRefs.current[i] = el
              }}
              className={`relative after:absolute after:-right-21 after:top-0 after:hidden after:h-full after:w-1 d:after:block d:last:after:hidden ${
                isDomainJournal ? 'after:bg-basic-gray7' : 'after:bg-dark-blue-dark-blue6'
              }`}
            >
              <JournalArticleItem
                date={date}
                title={articleTitle}
                url={url}
                tags={[
                  article.novelty && { label: translate('T_NOVELTY'), type: 'new' },
                  //article.openAccess && { label: translate('T_OPEN_ACCESS'), type: '' },
                  ...tags,
                ].filter(Boolean)}
                author={authors.join(', ')}
                variant={isDomainJournal ? 'journal-domain' : ''}
              />
            </li>
          )
        })}
      </ul>
      {articlesCount > visibleArticles && (
        <div className="flex justify-center">
          <Button
            type="button"
            size="l"
            variant="light"
            className={`mx-auto relative w-fit mt-88 ${
              isDomainJournal
                ? '!border-basic-black !text-basic-black supports-hover:hover:!border-basic-black supports-hover:hover:!bg-basic-gray8 supports-hover:hover:!text-basic-black'
                : ''
            }`}
            onClick={showMoreArticles}
          >
            {translate('T_SHOW_MORE')}
          </Button>
        </div>
      )}
    </section>
  )
}
